import { BathroomFinishingDto, BathroomFinishingSimpleDto } from '../../common-bathroom-dtos';
import { BathroomProductDto } from '../../tunnel/bathroom/bathroom-tunnel-dtos';
import { BasinType, BathroomCompositionDetailDto, CompositionRowDto } from './bathroom-composition-dtos';

export interface ConfigurationRule {
  id: string;
  name: string;
  description: string;
  validate: (context: ConfigurationContext) => boolean;
  errorMessage?: string;
}

export interface ConfigurationContext {
  composition: BathroomCompositionDetailDto;
  selectedWidth: number;
  selectedBasinType: BasinType;
  selectedDrawerPosition: 'Aucun' | 'À gauche' | 'À droite';
  currentRow?: CompositionRowDto;
}

export interface ConfigurationConstraint {
  parameter: 'width' | 'basinType' | 'drawerPosition';
  availableValues: any[];
  disabledValues: any[];
  reason?: string;
}

export interface AvailableOptions {
  widths: WidthOption[];
  basinTypes: BasinTypeOption[];
  drawerPositions: DrawerPositionOption[];
}

export interface WidthOption {
  value: number;
  available: boolean;
  reason?: string;
}

export interface BasinTypeOption {
  value: BasinType;
  label: string;
  available: boolean;
  reason?: string;
}

export interface DrawerPositionOption {
  value: 'Aucun' | 'À gauche' | 'À droite';
  available: boolean;
  reason?: string;
}

export enum HandleType {
  Integrated = 1,
  ToFix = 2,
}

// ============================================
// Configuration State
// ============================================

export interface BathroomConfigurationState {
  composition: BathroomCompositionDetailDto | null;
  isLoading: boolean;
  isPreviewLoading: boolean;
  isSaving: boolean;

  // Sélections globales
  selectedBasinType: BasinType;
  selectedBasinStyle: BasinStyle;

  // Dimension globale (si allowOffset = false)
  selectedGlobalWidth?: number;

  // Configuration par ligne
  rowConfigurations: RowConfiguration[];

  // Poignées (globales ou par colonne selon le besoin)
  handleConfiguration: HandleConfiguration;

  // Validation
  validationErrors: ValidationError[];
  isValid: boolean;

  // Prix
  totalPrice: number;

  // Options disponibles (calculées dynamiquement)
  availableOptions: AvailableConfigurationOptions;
}

export enum BasinStyle {
  WashbasinTop = 'Plan vasque',
  CountertopBasin = 'Vasque à poser',
}

export interface RowConfiguration {
  rowId: string;
  rowIndex: number;

  // Dimension sélectionnée pour cette rangée (si allowOffset = true)
  selectedWidth?: number;
  availableRowWidths: WidthOption[];

  // Offset entre les rangées (uniquement pour index > 0)
  offset: number;
  availableOffsets: number[];
  // Nouvelles propriétés pour champ libre d'offset
  hasOffsetOption: boolean; // true si l'offset initial > 0
  minOffset: number; // Min = 20
  maxOffset: number; // Max = largeur ligne dessus - 20

  // Configuration du plan de toilette (uniquement pour index === 0)
  coverFinitionId?: string;
  availableCoverFinitions: BathroomFinishingSimpleDto[];

  // Configuration des colonnes
  columnConfigurations: ColumnConfiguration[];
}

export interface ColumnConfiguration {
  columnId: string;
  columnIndex: number;

  // Dimension du tiroir
  width: number;

  // Finition du tiroir
  finitionId?: string;
  availableFinitions: BathroomFinishingSimpleDto[];

  // Référence au produit actuel
  currentProductId?: string;
  currentProduct?: BathroomProductDto;
}

export interface HandleConfiguration {
  // Type de poignée global
  handleType: HandleType;

  // Si "à fixer"
  handleLength?: number;
  handleFinitionId?: string;

  // Options disponibles
  availableHandleTypes: HandleTypeOption[];
  availableLengths: HandleLengthOption[];
  availableFinitions: BathroomFinishingSimpleDto[];
}

// ============================================
// Options disponibles
// ============================================

export interface AvailableConfigurationOptions {
  basinTypes: BasinTypeOption[];
  basinStyles: BasinStyleOption[];
  globalWidths: WidthOption[];
  // Indique si on peut choisir les dimensions par rangée ou globalement
  allowOffset: boolean;
  /**
   * Type d'affichage des dimensions :
   * - 'global': Dimension unique pour tout le meuble (1 rangée ou 2 rangées sans allowOffset)
   * - 'perRow': Dimension par rangée avec contraintes de combinaison (2 rangées avec allowOffset)
   */
  dimensionDisplayType: 'global' | 'perRow';
  /** Options de dimension pour le tiroir haut (quand dimensionDisplayType = 'perRow') */
  topRowWidths: WidthOption[];
  /** Options de dimension pour le tiroir bas (quand dimensionDisplayType = 'perRow') */
  bottomRowWidths: WidthOption[];
  /** Combinaisons de largeurs autorisées entre le haut et le bas (clé = largeur haut, valeur = largeurs bas compatibles) */
  widthCombinations?: Map<number, number[]>;
}

export interface WidthOption {
  value: number;
  available: boolean;
  reason?: string;
}

export interface BasinTypeOption {
  value: BasinType;
  label: string;
  available: boolean;
  reason?: string;
}

export interface BasinStyleOption {
  value: BasinStyle;
  available: boolean;
  reason?: string;
}

export interface HandleTypeOption {
  value: HandleType;
  label: string;
  imageUrl?: string;
  available: boolean;
  reason?: string;
}

export interface HandleLengthOption {
  value: number;
  label: string;
  available: boolean;
  reason?: string;
}

export interface FinitionOption {
  finition: BathroomFinishingSimpleDto;
  available: boolean;
  reason?: string;
}

// ============================================
// Validation
// ============================================

export interface ValidationError {
  code: string;
  message: string;
  field?: string;
  rowIndex?: number;
  columnIndex?: number;
}

export interface ValidationRule {
  id: string;
  rangeId?: string | null; // null ou undefined = s'applique à toutes les gammes
  name: string;
  description: string;
  validate: (state: BathroomConfigurationState) => ValidationResult;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ============================================
// Actions / Events
// ============================================

export type ConfigurationAction =
  | { type: 'SET_COMPOSITION'; payload: BathroomCompositionDetailDto }
  | { type: 'SET_BASIN_TYPE'; payload: BasinType }
  | { type: 'SET_BASIN_STYLE'; payload: BasinStyle }
  | { type: 'SET_ROW_OFFSET'; payload: { rowIndex: number; offset: number } }
  | { type: 'SET_COLUMN_WIDTH'; payload: { rowIndex: number; columnIndex: number; width: number } }
  | { type: 'SET_COLUMN_FINITION'; payload: { rowIndex: number; columnIndex: number; finitionId: string } }
  | { type: 'SET_COVER_FINITION'; payload: { rowIndex: number; finitionId: string } }
  | { type: 'SET_HANDLE_TYPE'; payload: HandleType }
  | { type: 'SET_HANDLE_LENGTH'; payload: number }
  | { type: 'SET_HANDLE_FINITION'; payload: string }
  | { type: 'UPDATE_PRODUCT'; payload: { rowIndex: number; columnIndex: number; product: BathroomProductDto } }
  | { type: 'UPDATE_COVER_PRODUCT'; payload: { rowIndex: number; product: BathroomProductDto } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_VALIDATION_ERRORS'; payload: ValidationError[] }
  | { type: 'RESET' };

// ============================================
// UI Selection State
// ============================================

export interface UISelectionState {
  // Élément actuellement sélectionné pour configuration
  selectedElement: SelectedElement | null;

  // Section active du formulaire
  activeSection: ConfigurationSection;

  // Mode d'affichage
  viewMode: 'compact' | 'expanded';
}

export type SelectedElement =
  | { type: 'column'; rowIndex: number; columnIndex: number }
  | { type: 'cover'; rowIndex: number }
  | { type: 'basin'; rowIndex: number }
  | { type: 'handle'; rowIndex: number; columnIndex: number };

export type ConfigurationSection = 'dimensions' | 'basin' | 'finitions' | 'handles' | 'positions';
